import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { FC } from "react";
import { Checkbox, Chip, CircularProgress, Paper, Tooltip, Typography, IconButton, Box } from "@mui/material";
import { Refresh as RefreshIcon, CheckCircle as CheckIcon, Error as ErrorIcon, HourglassEmpty as PendingIcon, Upload as UploadIcon, Delete as DeleteIcon, Warning as WarningIcon } from "@mui/icons-material";
import { dataTables } from "src/styles";
import { Bill } from "src/services/bill.service";
import { format } from "date-fns";

interface Props {
  data: Bill[];
  pageSize: number;
  currentPage: number;
  onPageChange: Function;
  onPageSizeChange: Function;
  loading?: boolean;
  selectedBills: Set<string>;
  onSelectBill: (guid: string) => void;
  canSelectBill: (bill: Bill) => boolean;
  onRetry?: (billGuid: string) => void;
  onDelete?: (billGuid: string) => void;
}

const BillTable: FC<Props> = (props: Props) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    }).format(amount / 100);
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), "dd MMM yyyy HH:mm");
  };

  const columns: GridColDef[] = [
    {
      field: "select",
      headerName: "",
      width: 60,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => {
        const bill = params.row.billData as Bill;
        const isSelectable = props.canSelectBill(bill);
        const isSelected = props.selectedBills.has(bill.guid);

        const tooltipMessage = !isSelectable
          ? bill.syncStatus !== 'SYNCED'
            ? `Only SYNCED bills can be merged (current: ${bill.syncStatus})`
            : "Cannot merge bills from different suppliers"
          : "";

        return (
          <Tooltip title={tooltipMessage} arrow>
            <span>
              <Checkbox
                checked={isSelected}
                onChange={() => props.onSelectBill(bill.guid)}
                disabled={!isSelectable}
              />
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: "billReference",
      headerName: "Reference",
      minWidth: 150,
      flex: 0.2,
      renderCell: (params: GridRenderCellParams) => {
        const bill = params.row.billData as Bill;
        // Only show link for bills that are synced, have a Xero invoice ID, and are NOT missing in Xero
        const isMissing = bill._missingInXero || bill.status === 'NOT_FOUND_IN_XERO';
        const xeroUrl = bill.xeroInvoiceId && bill.syncStatus === 'SYNCED' && !isMissing
          ? `https://go.xero.com/AccountsPayable/View.aspx?InvoiceID=${bill.xeroInvoiceId}`
          : null;

        return xeroUrl ? (
          <Typography
            variant="body2"
            fontFamily="monospace"
            component="a"
            href={xeroUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: "primary.main",
              textDecoration: "none",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
            title={`${params.row.billReference} - Click to open in Xero`}
          >
            {params.value}
          </Typography>
        ) : (
          <Typography variant="body2" fontFamily="monospace" title={params.row.billReference}>
            {params.value}
          </Typography>
        );
      },
    },
    {
      field: "supplierName",
      headerName: "Supplier",
      minWidth: 150,
      flex: 0.2,
      renderCell: (params: GridRenderCellParams) => {
        const supplierName = params.value || "Unknown";
        return (
          <Typography
            variant="body2"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              width: "100%"
            }}
            title={supplierName}
          >
            {supplierName}
          </Typography>
        );
      },
    },
    {
      field: "purchaseOrderNumbers",
      headerName: "PO Number(s)",
      minWidth: 150,
      flex: 0.2,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const poNumbers = Array.isArray(params.value) ? params.value.join(", ") : params.value;
        return (
          <Typography
            variant="body2"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              width: "100%"
            }}
            title={poNumbers}
          >
            {poNumbers}
          </Typography>
        );
      },
    },
    {
      field: "totalAmount",
      headerName: "Amount",
      minWidth: 120,
      flex: 0.1,
      align: "right",
      headerAlign: "right",
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" fontWeight="bold" title={params.row.billReference}>
          {formatCurrency(params.value as number)}
        </Typography>
      ),
    },
    {
      field: "createdDate",
      headerName: "Created",
      minWidth: 160,
      flex: 0.15,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" color="textSecondary" title={params.row.billReference}>
          {formatDate(params.value as string)}
        </Typography>
      ),
    },
    {
      field: "syncStatus",
      headerName: "Sync Status",
      minWidth: 130,
      flex: 0.12,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        const bill = params.row.billData as Bill;
        const status = bill.syncStatus || 'SYNCED';

        // Check if bill is missing in Xero (404 error)
        if (bill._missingInXero || bill.status === 'NOT_FOUND_IN_XERO') {
          return (
            <Tooltip title="This bill exists in AMS but could not be found in Xero. It may have been deleted, moved, or there may be a temporary API issue. Check Xero directly to verify.">
              <Chip
                icon={<WarningIcon />}
                label="Missing"
                size="small"
                color="error"
                variant="filled"
              />
            </Tooltip>
          );
        }

        if (status === 'SYNCED') {
          return (
            <Tooltip title="Successfully synced to Xero">
              <Chip
                icon={<CheckIcon />}
                label="Synced"
                size="small"
                color="success"
                variant="outlined"
              />
            </Tooltip>
          );
        } else if (status === 'FAILED') {
          return (
            <Tooltip title={bill.lastSyncError || 'Sync failed'}>
              <Chip
                icon={<ErrorIcon />}
                label="Failed"
                size="small"
                color="error"
                variant="outlined"
              />
            </Tooltip>
          );
        } else if (status === 'PENDING') {
          return (
            <Tooltip title="Sync in progress">
              <Chip
                icon={<PendingIcon />}
                label="Pending"
                size="small"
                color="warning"
                variant="outlined"
              />
            </Tooltip>
          );
        }
        return null;
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 100,
      flex: 0.1,
      align: "left",
      headerAlign: "left",
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => {
        const bill = params.row.billData as Bill;
        const isPending = bill.syncStatus === 'PENDING';
        const isFailed = bill.syncStatus === 'FAILED';
        const isMissing = bill._missingInXero || bill.status === 'NOT_FOUND_IN_XERO';
        const maxRetriesReached = bill.syncRetryCount >= 3;
        const notInXero = !bill.xeroInvoiceId; // Bill hasn't been sent to Xero yet

        // Show sync/retry button for PENDING and FAILED bills
        const showSyncButton = isPending || isFailed;

        // Show recreate button for missing bills
        const showRecreateButton = isMissing;

        // Determine sync icon and tooltip based on state
        let syncIcon = null;
        let syncTooltip = '';
        let syncDisabled = false;

        if (showSyncButton) {
          syncIcon = notInXero ? <UploadIcon fontSize="small" /> : <RefreshIcon fontSize="small" />;

          if (isPending) {
            syncTooltip = 'Sync to Xero (first attempt)';
          } else if (isFailed) {
            if (maxRetriesReached) {
              syncTooltip = `Max retries reached (${bill.syncRetryCount}/3). Manual intervention required.`;
              syncDisabled = true;
            } else {
              syncTooltip = `Retry sync to Xero (Attempt ${bill.syncRetryCount + 1}/3)`;
            }
          }
        }

        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {/* Delete Button - Always shown first */}
            <Tooltip title="Delete bill">
              <span>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => props.onDelete && props.onDelete(bill.guid)}
                  disabled={!props.onDelete}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            {/* Recreate Button - Shown for missing bills */}
            {showRecreateButton && (
              <Tooltip title="Recreate bill in Xero from receipt data">
                <span>
                  <IconButton
                    size="small"
                    color="default"
                    onClick={() => props.onRetry && props.onRetry(bill.guid)}
                    disabled={!props.onRetry}
                  >
                    <UploadIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}

            {/* Sync/Retry Button - Shown for PENDING/FAILED bills */}
            {showSyncButton && !showRecreateButton && (
              <Tooltip title={syncTooltip}>
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => props.onRetry && props.onRetry(bill.guid)}
                    disabled={syncDisabled || !props.onRetry}
                  >
                    {syncIcon}
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: "receiptCount",
      headerName: "Receipts",
      minWidth: 100,
      flex: 0.1,
      align: "center",
      headerAlign: "center",
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={`${params.value} receipt${params.value !== 1 ? "s" : ""}`}
          size="small"
          variant="outlined"
          title={params.row.billReference}
        />
      ),
    },
  ];

  const rows =
    props.data && props.data.length > 0
      ? props.data.map((bill: Bill, index: number) => {
          const isSelectable = props.canSelectBill(bill);
          return {
            id: bill.guid || index,
            guid: bill.guid,
            billReference: bill.billReference,
            supplierName: bill.supplierName,
            purchaseOrderNumbers: bill.purchaseOrderNumbers,
            totalAmount: bill.totalAmount,
            createdDate: bill.createdDate,
            receiptCount: bill.receiptGuids.length,
            billData: bill, // Store full bill object for selection logic
          };
        })
      : [];

  // Apply custom styling to rows based on selection state
  const getRowClassName = (params: any) => {
    const bill = params.row.billData as Bill;
    const isSelected = props.selectedBills.has(bill.guid);

    if (isSelected) {
      return "selected-row";
    }
    return "";
  };

  return (
    <Paper
      component="form"
      sx={{ p: "2px 4px", display: "flex", alignItems: "center" }}
    >
      <DataGrid
        loading={props.loading}
        slots={{ loadingOverlay: () => <CircularProgress /> }}
        rows={rows}
        columns={columns}
        paginationModel={{ page: props.currentPage, pageSize: props.pageSize }}
        density="compact"
        onPaginationModelChange={(model) => {
          props.onPageChange(model.page);
          props.onPageSizeChange(model.pageSize);
        }}
        disableRowSelectionOnClick
        getRowClassName={getRowClassName}
        sx={{
          ...dataTables,
          "& .selected-row": {
            backgroundColor: "action.selected",
          },
          "& .MuiDataGrid-cell": {
            display: "flex",
            alignItems: "center",
          },
          "& .MuiDataGrid-cell .MuiDataGrid-cellContent": {
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          },
        }}
      />
    </Paper>
  );
};

export default BillTable;
